import { useEffect, useMemo, useRef } from 'react';
import { WebSection } from '@wbk/contentful/api';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Typography } from '@wbk/ui';
import { Link } from 'react-router-dom';
import useLocalization from '@/context/localization/useLocalization';

gsap.registerPlugin(ScrollTrigger);

const TextInfo = ({
  date,
  title,
  subtitle,
  url,
}: {
  date: string;
  title: string;
  subtitle: string;
  url: string;
}) => {
  return (
    <div className='event-text absolute inset-0 z-10 flex flex-col items-center justify-center text-center text-white'>
      <Typography
        as='a'
        href={url}
        className='text-heading-L sm:text-heading-XL xl:text-heading-XXL font-bold uppercase'
        style={{
          cursor: url ? (url === '#' ? 'default' : 'pointer') : 'default',
        }}
      >
        {title}
      </Typography>
      <Typography
        className='md:text-body-S text-primary-100 py-4 font-semibold'
        style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)' }}
      >
        {date}
      </Typography>
      <Typography className='md:text-body-M max-w-sm'>{subtitle}</Typography>
    </div>
  );
};

export default function UpcomingEvents({ section }: { section: WebSection }) {
  const items = section.contentCollection.items;
  const { locale } = useLocalization();
  const containerRef = useRef<HTMLDivElement>(null);

  interface WebCardItem {
    id: string;
    title: string;
    subtitle: string;
    description: string;
    image?: { url: string };
    href: string;
  }

  const events = useMemo(() => {
    return (items as WebCardItem[]).map((item) => ({
      id: item.id,
      date: item.description || '',
      title: item.title || '',
      subtitle: item.subtitle || '',
      imageUrl: item.image?.url || '',
      href: item.href || '',
    }));
  }, [items]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const textElements = gsap.utils.toArray<HTMLElement>('.event-text');
      const imageElements = gsap.utils.toArray<HTMLImageElement>('.event-image');

      if (textElements.length === 0 || imageElements.length === 0) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: `+=${textElements.length * 2000}`,
          pin: true,
          scrub: 1,
        },
      });

      const fromPos = {
        yPercent: 50,
        xPercent: -50,
        left: '50%',
        top: '50%',
        scale: 0.4,
        opacity: 0,
        visibility: 'hidden',
      };

      const leftPos = {
        yPercent: -50,
        xPercent: -50,
        left: '24%',
        top: '50%',
        scale: 0.8,
        opacity: 1,
        visibility: 'visible',
      };

      const rightPos = {
        yPercent: -50,
        xPercent: -50,
        left: '75%',
        top: '50%',
        scale: 0.8,
        opacity: 1,
        visibility: 'visible',
      };

      const centerSmallPos = {
        yPercent: -50,
        xPercent: -50,
        left: '50%',
        top: '50%',
        scale: 0.25,
        opacity: 1,
        visibility: 'visible',
      };

      gsap.set(textElements, { opacity: 0, visibility: 'hidden', y: 40 });
      gsap.set(imageElements, { transformOrigin: 'center center' });

      for (let i = 0; i < textElements.length; i++) {
        const isEven = i % 2 === 0;
        const imageInPos = isEven ? leftPos : rightPos;

        tl.to(
          textElements[i],
          { opacity: 1, visibility: 'visible', y: 0, duration: 0.5 },
          '+=0.5'
        ).fromTo(imageElements[i], fromPos, { ...imageInPos, duration: 0.6 }, '<');

        tl.to(textElements[i], { opacity: 0, y: -40, duration: 0.3 }, '+=1').to(
          imageElements[i],
          { ...centerSmallPos, duration: 0.5 },
          '<'
        );
      }

      tl.to(
        [...textElements, ...imageElements],
        { opacity: 0, visibility: 'hidden', duration: 0.5 },
        '+=1'
      );
    }, containerRef);

    return () => ctx.revert();
  }, [events]);

  return (
    <div
      ref={containerRef}
      className='relative min-h-screen overflow-hidden'
      style={{
        backgroundImage: `url(${section.sectionBackground?.url || ''})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className='pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.9),transparent_30%,transparent_50%,rgba(0,0,0,0.9))]'></div>

      <div className='mx-auto'>
        <div className='container absolute inset-0 z-[999]'>
          {events.map((event) => (
            <Link
              to={`/${locale}${event.href}`}
              key={`image-${event.id}`}
              className='pointer-events-auto'
              style={{
                pointerEvents: 'all',
              }}
            >
              <img
                key={`img-${event.id}`}
                src={event.imageUrl}
                alt={event.title}
                className='event-image absolute h-[30%] w-full max-w-lg cursor-pointer object-contain sm:h-[45%] lg:h-[75%] xl:h-full'
                style={{
                  cursor: event.href ? (event.href === '#' ? 'default' : 'pointer') : 'default',
                }}
              />
            </Link>
          ))}
        </div>
      </div>

      <div className='relative flex h-full w-full items-end justify-center pb-20'>
        <div className='relative z-[999] h-48 w-full max-w-3xl'>
          {events.map((event) => (
            <TextInfo key={`text-${event.id}`} {...event} url={event.href} />
          ))}
        </div>
      </div>
    </div>
  );
}
